// ============================================================
// attack/misfire - what the roll does to the WEAPON: a jam (Reliable's
// confirmation die on a 00) and an overheat, and the entry a jam writes.
// ============================================================
import { attackBeat } from '../../engine/attackBeat';
import { calcJamThreshold, hasWS, getWSP, rollD100 } from '../../engine/index';
import type { NarrativeEntry } from '../../types';
import { OVERHEAT_DMG, targetNameOf, type Swing } from './armed';
import type { AttackContext } from './context';
import { writeHitlessOutcome } from './outcome';

/**
 * Official Reliable: a natural 00 must be CONFIRMED - a second d100 of 96+
 * upholds the jam, anything less is a cough and a miss.
 */
function reliableConfirms(notes: string[]): boolean {
  const confirm = rollD100();
  if (confirm >= 96) {
    notes.push('Reliable: 00 confirmed (' + confirm + ') - even this weapon can jam.');
    return true;
  }
  notes.push('Reliable: 00 unconfirmed (' + confirm + ' < 96) - a cough, not a jam.');
  return false;
}

/** Did the weapon jam? The aimed target's `jam` exposure (v8.368.1) lowers the band, and names itself when it set it. */
export function isJammed(ctx: AttackContext, sw: Swing, attackRoll: number): boolean {
  const jamFloor = sw.onHit.jam;
  const jamOpts = { suppressing: ctx.suppressing };
  const jamThresh = calcJamThreshold(sw.sp, sw.isMelee, ctx.reliableNeverJams, { ...jamOpts, jamFloor });
  if (
    !sw.isMelee &&
    jamFloor > 0 &&
    jamThresh === jamFloor &&
    jamFloor < calcJamThreshold(sw.sp, false, ctx.reliableNeverJams, jamOpts)
  )
    sw.notes.push(`Exposed: jams on ${jamFloor}+.`);
  const isJam = !sw.isMelee && attackRoll >= jamThresh;
  return isJam && hasWS(sw.sp, 'Reliable') ? reliableConfirms(sw.notes) : isJam;
}

/**
 * Official Overheats (v8.198.1): the wielder chooses - drop the weapon or
 * take its OWN damage - in a prompt the component owns. The target's
 * `overheat` exposure (v8.371.1) lowers the threshold, Overheats weapon or
 * not; the lower one wins, like a jam floor.
 */
export function checkOverheat(ctx: AttackContext, sw: Swing, attackRoll: number): void {
  const ownOverheat = hasWS(sw.sp, 'Overheats') ? getWSP(sw.sp, 'Overheats') || 91 : 101;
  const exposed = sw.onHit.overheat;
  const overheatThresh = exposed > 0 ? Math.min(ownOverheat, exposed) : ownOverheat;
  if (sw.isMelee || attackRoll < overheatThresh) return;
  if (overheatThresh < ownOverheat) sw.notes.push(`Exposed: overheats on ${exposed}+.`);
  sw.notes.push('Overheats! Drop the weapon to spare the arm, or hold on and take its full fury.');
  ctx.onOverheat?.({
    slotName: sw.slot.weaponName,
    damageExpression: sw.slot.damageExpression,
    damageType: sw.slot.damageType,
  });
}

export function writeJam(ctx: AttackContext, sw: Swing, attackRoll: number): void {
  const at = Date.now();
  const jamEntry: NarrativeEntry = {
    type: 'attack-result' as const,
    result: { jam: true, attackRoll, success: false, overheatDmg: OVERHEAT_DMG },
    slotName: sw.slot.weaponName,
    fireMode: ctx.fireMode,
    targetName: targetNameOf(sw.tab),
    timestamp: at,
    beat: attackBeat(ctx.actorId, sw.isMelee, 'jam', ctx.activeTab, at, {
      specials: sw.slot.specialRules,
      ...sw.beatKind,
    }),
  };
  writeHitlessOutcome(ctx, ctx.activeTab, jamEntry, sw.cdResult);
}
